import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { EMPTY, lastValueFrom, Observable, Subject } from 'rxjs';

import { IChapter } from '@vbrick/vbrick-video-editor/app/editor/Contract';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { ITranscriptionFile } from 'rev-shared/media/ITranscriptionFile';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { PushService } from 'rev-shared/push/PushService';

import {
	GenerateMetadataStatus,
	IGenerateMetadataOptionSignalMap,
	GenerateMetadataOption,
	VideoMetadataGenerationJobSubmittedEvent,
	VideoMetadataGenerationJobFinishedEvent,
	MetadataGenerationJobStatus,
	VideoMetadataGenerationJobFailedEvent,
	IChapterMetadata
} from './VideoAiMetadataTypes';

export const MetadataGenerationRouteScope = 'Media.MetadataGeneration';

@Injectable({
	providedIn: 'root'
})
export class VideoAiMetadataService {

	private readonly fieldsUpdatedSubject = new Subject();
	public readonly fieldsUpdated$ = this.fieldsUpdatedSubject.asObservable();

	public readonly generateTitleSignal: WritableSignal<GenerateMetadataStatus> = signal(GenerateMetadataStatus.HIDDEN);
	public readonly generateDescriptionSignal: WritableSignal<GenerateMetadataStatus> = signal(GenerateMetadataStatus.HIDDEN);
	public readonly generateTagsSignal: WritableSignal<GenerateMetadataStatus> = signal(GenerateMetadataStatus.HIDDEN);
	public readonly generateChaptersSignal: WritableSignal<GenerateMetadataStatus> = signal(GenerateMetadataStatus.HIDDEN);
	private readonly generateMetadataOptionSignalMap: IGenerateMetadataOptionSignalMap = {
		[GenerateMetadataOption.TITLE]: this.generateTitleSignal,
		[GenerateMetadataOption.DESCRIPTION]: this.generateDescriptionSignal,
		[GenerateMetadataOption.TAGS]: this.generateTagsSignal,
		[GenerateMetadataOption.CHAPTERS]: this.generateChaptersSignal
	};
	public readonly generateAllBtnSignal: Signal<GenerateMetadataStatus> = computed(() => {
		return Object.values(this.generateMetadataOptionSignalMap).reduce((acc, signal) => {
			//always call all 4 signals to set up the dependency chain.
			const val = signal();
			return acc && val === GenerateMetadataStatus.HIDDEN || val === GenerateMetadataStatus.PROCESSING;
		}, true)
			? GenerateMetadataStatus.PROCESSING
			: GenerateMetadataStatus.ENABLED;
	});

	public readonly isGenerateAllBtnVisibleSignal: Signal<boolean> = computed(() => {
		return Object.values(this.generateMetadataOptionSignalMap).reduce((acc, option) =>
			//Ensure each signal is called, skipping a signal() call would corrupt the dependency chain.
			option() !== GenerateMetadataStatus.HIDDEN || acc
		, false);
	});

	constructor(
		private DateParser: DateParsersService,
		private MediaFeatures: MediaFeaturesService,
		private PushService: PushService,
		private PushBus: PushBus,
		private http: HttpClient
	) { }

	public getPushSubscriptions(
		video: any
	): Observable<any> {
		return this.PushBus.getObservable(video.id, MetadataGenerationRouteScope, {

			VideoMetadataGenerationJobSubmitted: (e: VideoMetadataGenerationJobSubmittedEvent) => {
				this.updateGenerateMetadataStates(e.metadataGenerationFields, GenerateMetadataStatus.PROCESSING);
			},

			VideoMetadataGenerationJobFinished: (e: VideoMetadataGenerationJobFinishedEvent) => {
				this.updateGenerateMetadataStates(e.metadataGenerationFields, GenerateMetadataStatus.ENABLED);
				if (video) {
					video.title = e.title || video.title;
					video.description = e.description || video.description;
					video.chapters = e.videoChapters ? this.formatChapterMetadata(e.videoChapters) : video.chapters;
					if (this.MediaFeatures.accountFeatures.enableTags && e.tags?.length) {
						video.tags = Array.from(new Set([...video.tags, ...e.tags.map(tag => tag.trim())]));
					}
				}
				this.fieldsUpdatedSubject.next(e.metadataGenerationFields);
				return EMPTY;
			},

			VideoMetadataGenerationJobFailed: (e: VideoMetadataGenerationJobFailedEvent) => {
				this.resetGenerateMetadataStates();
				console.error('Metadata generation failed: ', e);
				return EMPTY;
			}
		});
	}

	private formatChapterMetadata(chapters: IChapterMetadata[]): IChapter[] {
		return chapters.map(chapterMetadata => {
			return {
				...chapterMetadata,
				time: this.DateParser.parseTimespan(chapterMetadata.time),
			} as IChapter;
		});
	}


	public initializeAiMetadataStatus(videoId: string): Promise<void> {
		this.resetGenerateMetadataStates();
		return lastValueFrom(this.http.get<any>(`/media/videos/${videoId}/metadata-generation-status`))
			.then(generationStatus => {
				if (!generationStatus.aiMetadataGenerationStatus) {
					return;
				}
				Object.keys(generationStatus.aiMetadataGenerationStatus).forEach((option: GenerateMetadataOption) => {
					let status;
					switch(generationStatus.aiMetadataGenerationStatus[option]) {
						case MetadataGenerationJobStatus.INPROGRESS:
							status = GenerateMetadataStatus.PROCESSING;
							break;
						case MetadataGenerationJobStatus.SUCCESS:
						case MetadataGenerationJobStatus.FAILED:
							status = GenerateMetadataStatus.ENABLED;
							break;
					}
					this.generateMetadataOptionSignalMap[option]?.set(status);
				});
			});
	}

	public generateVideoDetailsMetadata(videoId: string, transcriptionFile: ITranscriptionFile, options: GenerateMetadataOption[]): Promise<any> {
		this.updateGenerateMetadataStates(options, GenerateMetadataStatus.PROCESSING);
		return this.PushService.dispatchCommand('media:StartVideoMetadataGeneration', {
			videoId,
			transcriptionFile,
			transcriptionLanguage: 'en',
			metadataGenerationFields: options
		});
	}

	public updateGenerateMetadataStates(generateOptions: GenerateMetadataOption[], state: GenerateMetadataStatus) {
		generateOptions.forEach(option => {
			this.generateMetadataOptionSignalMap[option]?.set(state);
		});
	}

	private resetGenerateMetadataStates() {
		Object.values(this.generateMetadataOptionSignalMap)
			.forEach(signal => signal.set(GenerateMetadataStatus.ENABLED));
	}

	public getGenerateAllMetadataOptions(): GenerateMetadataOption[] {
		return Object.keys(this.generateMetadataOptionSignalMap)
			.filter((option: GenerateMetadataOption) => {
				const state = this.generateMetadataOptionSignalMap[option]();
				return state !== GenerateMetadataStatus.PROCESSING && state !== GenerateMetadataStatus.HIDDEN;
			}) as GenerateMetadataOption[];
	}

	public getSignalByMetadataOption(option: GenerateMetadataOption): WritableSignal<GenerateMetadataStatus> {
		return this.generateMetadataOptionSignalMap[option];
	}

}
